<template>
  <div class="cert-manage-page">
    <div class="page-header">
      <div class="title-area">
        <h2 class="page-title">인증서 관리</h2>
        <p class="page-subtitle">롱텀케어·희망이음 공인인증서 조회 및 관리 (최고관리자 전용)</p>
      </div>
      <div class="my-ip-area">
        <div class="my-ip-box">
          <v-icon size="18" class="ip-icon">mdi-web</v-icon>
          <span class="ip-label">현재 접속 IP:</span>
          <span class="ip-value">{{ myIP || '조회중...' }}</span>
          <v-btn
            icon
            variant="text"
            size="x-small"
            @click="copyMyIP"
            title="IP 복사"
          >
            <v-icon size="16">mdi-content-copy</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <v-tabs v-model="activeTab" class="cert-type-tabs" density="compact">
      <v-tab value="ltcs">롱텀케어 공인인증서</v-tab>
      <v-tab value="hope">희망이음 공동인증서</v-tab>
    </v-tabs>

    <div class="cert-body">
      <!-- 롱텀케어 탭 -->
      <template v-if="activeTab === 'ltcs'">
      <div class="cert-main-layout">
        <div class="content-card">
          <div class="search-area">
            <v-text-field
              v-model="searchKeyword"
              placeholder="기관명, 인증서명 검색"
              variant="outlined"
              density="compact"
              hide-details
              prepend-inner-icon="mdi-magnify"
              class="search-input"
              clearable
            />
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              variant="outlined"
              density="compact"
              hide-details
              class="status-select"
            />
          </div>

          <v-data-table
            :headers="headers"
            :items="filteredCerts"
            :loading="loading"
            class="cert-table"
            hover
            @click:row="(_: any, { item }: any) => selectCert(item)"
          >
            <template #item.m_name="{ item }">
              <div class="facility-info">
                <span class="facility-name">{{ item.m_name || '-' }}</span>
                <span class="facility-code">{{ item.m_longterm_code || '' }}</span>
              </div>
            </template>

            <template #item.m_biz_gubun="{ item }">
              <v-chip
                v-for="biz in (item.m_biz_gubun || '').split(',')"
                :key="biz"
                size="x-small"
                class="biz-chip"
              >
                {{ biz.trim() }}
              </v-chip>
            </template>

            <template #item.cic_cert_name="{ item }">
              {{ item.cic_cert_name || '-' }}
            </template>

            <template #item.cic_expire_date="{ item }">
              <v-chip
                :color="getExpireColor(item.cic_expire_date)"
                size="small"
                variant="flat"
              >
                {{ formatDate(item.cic_expire_date) }}
              </v-chip>
            </template>

            <template #item.cic_status="{ item }">
              <v-chip
                :color="getStatusColor(item.cic_status)"
                size="small"
                variant="flat"
              >
                {{ getStatusText(item.cic_status) }}
              </v-chip>
            </template>

            <template #item.ip_count="{ item }">
              <v-chip
                color="primary"
                size="small"
                variant="outlined"
                @click.stop="openIpModal(item)"
              >
                {{ item.ip_count }}개
              </v-chip>
            </template>

            <template #item.cic_last_used="{ item }">
              {{ item.cic_last_used ? formatDateTime(item.cic_last_used) : '-' }}
            </template>

            <template #item.cic_wdate="{ item }">
              {{ formatDate(item.cic_wdate) }}
            </template>

            <template #item.actions="{ item }">
              <v-btn
                v-if="item.cic_status === 'active'"
                variant="flat"
                class="btn-table-download"
                size="small"
                :loading="downloadingLtcsCode === item.m_code"
                @click.stop="downloadLtcsCertZip(item)"
              >
                <v-icon size="14">mdi-download</v-icon>
                ZIP
              </v-btn>
              <span v-else class="action-disabled">-</span>
            </template>
          </v-data-table>
        </div>
      </div>

      <div v-if="selectedCert" class="cert-detail-sidebar">
        <div class="sidebar-header">
          <h3>인증서 상세</h3>
          <v-btn icon variant="text" @click="selectedCert = null">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="sidebar-content">
          <div v-if="selectedCert.cic_status === 'active'" class="sidebar-download-bar">
            <v-btn
              variant="flat"
              class="btn-download"
              :loading="downloadingLtcsCode === selectedCert.m_code"
              @click="downloadLtcsCertZip()"
            >
              <v-icon size="16" class="mr-1">mdi-download</v-icon>
              인증서 ZIP 다운로드
            </v-btn>
          </div>

          <div class="detail-section">
            <h4>기관 정보</h4>
            <div class="detail-row">
              <span class="label">기관명</span>
              <span class="value">{{ selectedCert.m_name }}</span>
            </div>
            <div class="detail-row">
              <span class="label">기관코드</span>
              <span class="value">{{ selectedCert.m_longterm_code }}</span>
            </div>
            <div class="detail-row">
              <span class="label">m_code</span>
              <span class="value mono">{{ selectedCert.m_code }}</span>
            </div>
            <div class="detail-row">
              <span class="label">사업구분</span>
              <span class="value">{{ selectedCert.m_biz_gubun }}</span>
            </div>
          </div>

          <div class="detail-section">
            <h4>인증서 정보</h4>
            <div class="detail-row">
              <span class="label">발급자명</span>
              <span class="value">{{ selectedCert.cic_cert_name || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="label">만료일</span>
              <span class="value">{{ formatDate(selectedCert.cic_expire_date) }}</span>
            </div>
            <div class="detail-row">
              <span class="label">상태</span>
              <v-chip
                :color="getStatusColor(selectedCert.cic_status)"
                size="small"
                variant="flat"
              >
                {{ getStatusText(selectedCert.cic_status) }}
              </v-chip>
            </div>
            <div class="detail-row">
              <span class="label">업로드 IP</span>
              <span class="value">{{ selectedCert.cic_upload_ip || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="label">등록일</span>
              <span class="value">{{ formatDateTime(selectedCert.cic_wdate) }}</span>
            </div>
            <div class="detail-row">
              <span class="label">마지막 사용</span>
              <span class="value">{{ selectedCert.cic_last_used ? formatDateTime(selectedCert.cic_last_used) : '-' }}</span>
            </div>
          </div>

          <div v-if="selectedCert.cic_status === 'active'" class="detail-section ltcs-secrets-section">
            <h4>비밀정보</h4>
            <LoadingOverlay :show="ltcsSecretsLoading" text="비밀정보 불러오는 중..." variant="light" />
            <template v-if="ltcsSecrets">
              <div class="secret-row">
                <span class="label">인증서 비밀번호</span>
                <div class="secret-value-wrap">
                  <span class="secret-value">{{ showLtcsCertPw ? ltcsSecrets.certPassword : '••••••••' }}</span>
                  <button type="button" class="secret-toggle-btn" @click="showLtcsCertPw = !showLtcsCertPw">
                    <v-icon size="16">{{ showLtcsCertPw ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                  </button>
                </div>
              </div>
            </template>
            <div v-else-if="!ltcsSecretsLoading" class="no-ips">
              {{ ltcsSecretsError || '비밀번호를 불러오지 못했습니다.' }}
            </div>
          </div>
          <div v-else class="detail-section">
            <p class="inactive-cert-hint">활성 상태 인증서만 비밀정보 조회 및 다운로드가 가능합니다.</p>
          </div>

          <div class="detail-section">
            <div class="section-header">
              <h4>허용 IP 목록</h4>
              <v-btn
                size="small"
                variant="flat"
                class="btn-register"
                @click="openAddIpDialog"
              >
                <v-icon size="small" class="mr-1">mdi-plus</v-icon>
                IP 추가
              </v-btn>
            </div>

            <div v-if="loadingIPs" class="ip-loading">
              <v-progress-circular indeterminate size="24" />
            </div>

            <div v-else-if="allowedIPs.length === 0" class="no-ips">
              등록된 허용 IP가 없습니다.
            </div>

            <div v-else class="ip-list">
              <div
                v-for="ip in allowedIPs"
                :key="ip.ccai_idx"
                class="ip-item"
                :class="{ inactive: !ip.ccai_is_active }"
              >
                <div class="ip-info">
                  <span class="ip-address">{{ ip.ccai_ip }}</span>
                  <span v-if="ip.ccai_description" class="ip-desc">{{ ip.ccai_description }}</span>
                </div>
                <div class="ip-actions">
                  <v-switch
                    :model-value="!!ip.ccai_is_active"
                    hide-details
                    density="compact"
                    color="primary"
                    @update:model-value="toggleIP(ip, $event as boolean)"
                  />
                  <v-btn
                    icon
                    variant="text"
                    size="small"
                    color="error"
                    @click="confirmDeleteIP(ip)"
                  >
                    <v-icon size="small">mdi-delete</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </template>

      <!-- 희망이음 탭 -->
      <template v-else>
      <div class="cert-main-layout">
        <div class="content-card">
          <div class="search-area">
            <v-text-field
              v-model="hopeSearchKeyword"
              placeholder="기관명, 인증서명, 기관코드 검색"
              variant="outlined"
              density="compact"
              hide-details
              prepend-inner-icon="mdi-magnify"
              class="search-input"
              clearable
            />
            <v-select
              v-model="hopeStatusFilter"
              :items="statusOptions"
              variant="outlined"
              density="compact"
              hide-details
              class="status-select"
            />
          </div>

          <div class="hope-table-wrap">
            <LoadingOverlay :show="hopeLoading" text="불러오는 중..." />
            <v-data-table
              :headers="hopeHeaders"
              :items="filteredHopeCerts"
              class="cert-table"
              hover
              @click:row="(_: any, { item }: any) => selectHopeCert(item)"
            >
              <template #item.m_name="{ item }">
                <div class="facility-info">
                  <span class="facility-name">{{ item.m_name || '-' }}</span>
                  <span class="facility-code">{{ item.m_longterm_code || item.m_code || '' }}</span>
                </div>
              </template>

              <template #item.m_biz_gubun="{ item }">
                <v-chip
                  v-for="biz in (item.m_biz_gubun || '').split(',')"
                  :key="biz"
                  size="x-small"
                  class="biz-chip"
                >
                  {{ biz.trim() }}
                </v-chip>
              </template>

              <template #item.chc_cert_name="{ item }">
                {{ item.chc_cert_name || '-' }}
              </template>

              <template #item.chc_expire_date="{ item }">
                <v-chip
                  :color="getExpireColor(item.chc_expire_date)"
                  size="small"
                  variant="flat"
                >
                  {{ formatDate(item.chc_expire_date) }}
                </v-chip>
              </template>

              <template #item.chc_status="{ item }">
                <v-chip
                  :color="getStatusColor(item.chc_status)"
                  size="small"
                  variant="flat"
                >
                  {{ getStatusText(item.chc_status) }}
                </v-chip>
              </template>

              <template #item.chc_last_used="{ item }">
                {{ item.chc_last_used ? formatDateTime(item.chc_last_used) : '-' }}
              </template>

              <template #item.chc_wdate="{ item }">
                {{ formatDate(item.chc_wdate) }}
              </template>

              <template #item.actions="{ item }">
                <v-btn
                  v-if="item.chc_status === 'active'"
                  variant="flat"
                  class="btn-table-download"
                  size="small"
                  :loading="downloadingHopeCode === item.m_code"
                  @click.stop="downloadHopeCertZip(item)"
                >
                  <v-icon size="14">mdi-download</v-icon>
                  ZIP
                </v-btn>
                <span v-else class="action-disabled">-</span>
              </template>
            </v-data-table>
          </div>
        </div>
      </div>

      <div v-if="selectedHopeCert" class="cert-detail-sidebar hope-cert-sidebar">
        <div class="sidebar-header">
          <h3>희망이음 인증서</h3>
          <v-btn icon variant="text" @click="closeHopeSidebar">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="sidebar-content">
          <div v-if="selectedHopeCert.chc_status === 'active'" class="sidebar-download-bar">
            <v-btn
              variant="flat"
              class="btn-download"
              :loading="downloadingHopeCode === selectedHopeCert.m_code"
              @click="downloadHopeCertZip()"
            >
              <v-icon size="16" class="mr-1">mdi-download</v-icon>
              인증서 ZIP 다운로드
            </v-btn>
          </div>

          <div class="detail-section">
            <h4>기관 정보</h4>
            <div class="detail-row">
              <span class="label">기관명</span>
              <span class="value">{{ selectedHopeCert.m_name || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="label">장기요양기관기호</span>
              <span class="value">{{ selectedHopeCert.m_longterm_code || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="label">m_code</span>
              <span class="value mono">{{ selectedHopeCert.m_code }}</span>
            </div>
            <div class="detail-row">
              <span class="label">사업구분</span>
              <span class="value">{{ selectedHopeCert.m_biz_gubun || '-' }}</span>
            </div>
          </div>

          <div class="detail-section">
            <h4>인증서 정보</h4>
            <div class="detail-row">
              <span class="label">발급자명</span>
              <span class="value">{{ selectedHopeCert.chc_cert_name || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="label">만료일</span>
              <span class="value">{{ formatDate(selectedHopeCert.chc_expire_date) }}</span>
            </div>
            <div class="detail-row">
              <span class="label">상태</span>
              <v-chip
                :color="getStatusColor(selectedHopeCert.chc_status)"
                size="small"
                variant="flat"
              >
                {{ getStatusText(selectedHopeCert.chc_status) }}
              </v-chip>
            </div>
            <div class="detail-row">
              <span class="label">등록일</span>
              <span class="value">{{ formatDateTime(selectedHopeCert.chc_wdate) }}</span>
            </div>
            <div class="detail-row">
              <span class="label">마지막 사용</span>
              <span class="value">{{ selectedHopeCert.chc_last_used ? formatDateTime(selectedHopeCert.chc_last_used) : '-' }}</span>
            </div>
          </div>

          <div v-if="selectedHopeCert.chc_status === 'active'" class="detail-section hope-secrets-section">
            <h4>비밀정보</h4>
            <LoadingOverlay :show="hopeSecretsLoading" text="비밀정보 불러오는 중..." variant="light" />
            <template v-if="hopeSecrets">
              <div class="secret-row">
                <span class="label">인증서 비밀번호</span>
                <div class="secret-value-wrap">
                  <span class="secret-value">{{ showHopeCertPw ? hopeSecrets.certPassword : '••••••••' }}</span>
                  <button type="button" class="secret-toggle-btn" @click="showHopeCertPw = !showHopeCertPw">
                    <v-icon size="16">{{ showHopeCertPw ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                  </button>
                </div>
              </div>
              <div class="secret-row">
                <span class="label">희망이음 아이디</span>
                <span class="secret-value">{{ hopeSecrets.hopeId || '-' }}</span>
              </div>
              <div class="secret-row">
                <span class="label">희망이음 비밀번호</span>
                <div class="secret-value-wrap">
                  <span class="secret-value">{{ showHopeHopePw ? hopeSecrets.hopePw : '••••••••' }}</span>
                  <button type="button" class="secret-toggle-btn" @click="showHopeHopePw = !showHopeHopePw">
                    <v-icon size="16">{{ showHopeHopePw ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                  </button>
                </div>
              </div>
            </template>
            <div v-else-if="!hopeSecretsLoading" class="no-ips">
              {{ hopeSecretsError || '비밀번호를 불러오지 못했습니다. 위 다운로드 버튼은 사용 가능합니다.' }}
            </div>
          </div>
          <div v-else class="detail-section">
            <p class="inactive-cert-hint">활성 상태 인증서만 비밀정보 조회 및 다운로드가 가능합니다.</p>
          </div>
        </div>
      </div>
      </template>
    </div>

    <v-dialog v-model="showAddIpDialog" max-width="400">
      <v-card>
        <v-card-title class="dialog-title">
          <span>IP 추가</span>
          <button
            type="button"
            class="dialog-title__close"
            :disabled="addingIP"
            aria-label="닫기"
            @click="showAddIpDialog = false"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text>
          <div class="ip-input-row">
            <v-text-field
              v-model="newIP.ip"
              label="IP 주소"
              placeholder="예: 123.456.789.0"
              variant="outlined"
              density="compact"
              hide-details
            />
            <v-btn
              variant="outlined"
              size="small"
              class="apply-my-ip-btn"
              @click="applyMyIP"
              :disabled="!myIP"
            >
              <v-icon size="16" class="mr-1">mdi-crosshairs-gps</v-icon>
              현재주소
            </v-btn>
          </div>
          <v-text-field
            v-model="newIP.description"
            label="설명 (선택)"
            placeholder="예: 본사 PC, 김대리 노트북"
            variant="outlined"
            density="compact"
            class="mt-3"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" class="btn-cancel" @click="showAddIpDialog = false">취소</v-btn>
          <v-spacer />
          <v-btn variant="flat" class="btn-save" @click="addIP" :loading="addingIP">추가</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDeleteIpDialog" max-width="360">
      <v-card>
        <v-card-title class="dialog-title">
          <span>IP 삭제</span>
          <button
            type="button"
            class="dialog-title__close"
            :disabled="deletingIP"
            aria-label="닫기"
            @click="showDeleteIpDialog = false"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text>
          <p>다음 IP를 삭제하시겠습니까?</p>
          <p class="ip-to-delete">{{ ipToDelete?.ccai_ip }}</p>
          <p v-if="ipToDelete?.ccai_description" class="ip-desc-to-delete">{{ ipToDelete.ccai_description }}</p>
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" class="btn-cancel" @click="showDeleteIpDialog = false">취소</v-btn>
          <v-spacer />
          <v-btn variant="flat" class="btn-delete" @click="deleteIP" :loading="deletingIP">삭제</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import { certApi, hopeCertApi } from '@/services/api';

interface CertItem {
  cic_idx: number;
  m_code: string;
  m_name: string;
  m_longterm_code: string;
  m_biz_gubun: string;
  cic_cert_name: string;
  cic_expire_date: string;
  cic_status: string;
  cic_last_used: string;
  cic_upload_ip: string;
  cic_wdate: string;
  ip_count: number;
}

interface HopeCertItem {
  chc_idx: number;
  m_code: string;
  m_name: string;
  m_longterm_code: string;
  m_biz_gubun: string;
  chc_cert_name: string;
  chc_expire_date: string;
  chc_status: string;
  chc_last_used: string;
  chc_upload_ip: string;
  chc_wdate: string;
}

interface AllowedIP {
  ccai_idx: number;
  cic_idx: number;
  ccai_ip: string;
  ccai_description: string;
  ccai_is_active: number;
  ccai_wdate: string;
}

const activeTab = ref<'ltcs' | 'hope'>('ltcs');

const loading = ref(false);
const certs = ref<CertItem[]>([]);
const searchKeyword = ref('');
const statusFilter = ref('all');
const selectedCert = ref<CertItem | null>(null);
const myIP = ref('');

const ltcsSecrets = ref<{
  certPassword: string;
  hasFiles: boolean;
} | null>(null);
const ltcsSecretsLoading = ref(false);
const ltcsSecretsError = ref('');
const downloadingLtcsCode = ref('');
const showLtcsCertPw = ref(false);

const hopeLoading = ref(false);
const hopeCerts = ref<HopeCertItem[]>([]);
const hopeSearchKeyword = ref('');
const hopeStatusFilter = ref('active');
const selectedHopeCert = ref<HopeCertItem | null>(null);
const hopeSecrets = ref<{
  certPassword: string;
  hopeId: string;
  hopePw: string;
  hasFiles: boolean;
} | null>(null);
const hopeSecretsLoading = ref(false);
const hopeSecretsError = ref('');
const downloadingHopeCode = ref('');
const showHopeCertPw = ref(false);
const showHopeHopePw = ref(false);

const allowedIPs = ref<AllowedIP[]>([]);
const loadingIPs = ref(false);

const showAddIpDialog = ref(false);
const addingIP = ref(false);
const newIP = ref({ ip: '', description: '' });

const showDeleteIpDialog = ref(false);
const deletingIP = ref(false);
const ipToDelete = ref<AllowedIP | null>(null);

const statusOptions = [
  { title: '전체 상태', value: 'all' },
  { title: '활성', value: 'active' },
  { title: '만료됨', value: 'expired' },
  { title: '폐기됨', value: 'revoked' }
];

const headers = [
  { title: '기관명', key: 'm_name', sortable: true },
  { title: '사업구분', key: 'm_biz_gubun', sortable: false },
  { title: '인증서명', key: 'cic_cert_name', sortable: true },
  { title: '만료일', key: 'cic_expire_date', sortable: true },
  { title: '상태', key: 'cic_status', sortable: true },
  { title: '허용 IP', key: 'ip_count', sortable: false },
  { title: '마지막 사용', key: 'cic_last_used', sortable: true },
  { title: '등록일', key: 'cic_wdate', sortable: true },
  { title: '다운로드', key: 'actions', sortable: false },
];

const hopeHeaders = [
  { title: '기관명', key: 'm_name', sortable: true },
  { title: '사업구분', key: 'm_biz_gubun', sortable: false },
  { title: '인증서명', key: 'chc_cert_name', sortable: true },
  { title: '만료일', key: 'chc_expire_date', sortable: true },
  { title: '상태', key: 'chc_status', sortable: true },
  { title: '마지막 사용', key: 'chc_last_used', sortable: true },
  { title: '등록일', key: 'chc_wdate', sortable: true },
  { title: '다운로드', key: 'actions', sortable: false },
];

const filteredCerts = computed(() => {
  let result = [...certs.value];

  if (statusFilter.value !== 'all') {
    result = result.filter(c => c.cic_status === statusFilter.value);
  }

  if (searchKeyword.value) {
    const keyword = searchKeyword.value.toLowerCase();
    result = result.filter(c =>
      (c.m_name || '').toLowerCase().includes(keyword) ||
      (c.cic_cert_name || '').toLowerCase().includes(keyword) ||
      (c.m_longterm_code || '').toLowerCase().includes(keyword)
    );
  }

  return result;
});

const filteredHopeCerts = computed(() => {
  let result = [...hopeCerts.value];

  if (hopeStatusFilter.value !== 'all') {
    result = result.filter((c) => c.chc_status === hopeStatusFilter.value);
  }

  if (hopeSearchKeyword.value) {
    const keyword = hopeSearchKeyword.value.toLowerCase();
    result = result.filter((c) =>
      (c.m_name || '').toLowerCase().includes(keyword) ||
      (c.chc_cert_name || '').toLowerCase().includes(keyword) ||
      (c.m_longterm_code || '').toLowerCase().includes(keyword) ||
      (c.m_code || '').toLowerCase().includes(keyword)
    );
  }

  return result;
});

const formatDate = (date: string): string => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('ko-KR');
};

const formatDateTime = (date: string): string => {
  if (!date) return '-';
  return new Date(date).toLocaleString('ko-KR');
};

const getExpireColor = (expireDate: string): string => {
  if (!expireDate) return 'grey';
  const diff = new Date(expireDate).getTime() - Date.now();
  const days = diff / (1000 * 60 * 60 * 24);
  if (days < 0) return 'error';
  if (days < 30) return 'warning';
  return 'success';
};

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'active': return 'success';
    case 'expired': return 'error';
    case 'revoked': return 'grey';
    case 'deleted': return 'grey';
    default: return 'grey';
  }
};

const getStatusText = (status: string): string => {
  switch (status) {
    case 'active': return '활성';
    case 'expired': return '만료됨';
    case 'revoked': return '폐기됨';
    case 'deleted': return '삭제됨';
    default: return status;
  }
};

const loadCerts = async () => {
  loading.value = true;
  try {
    const res = await certApi.getAllCerts();
    certs.value = res.data.data || [];
  } catch (error: any) {
    const msg = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`롱텀케어 인증서 목록 조회 실패: ${msg}`);
  } finally {
    loading.value = false;
  }
};

const loadHopeCerts = async () => {
  hopeLoading.value = true;
  try {
    const res = await hopeCertApi.getAllCerts();
    hopeCerts.value = res.data?.data || [];
  } catch (error: any) {
    const msg = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`희망이음 인증서 목록 조회 실패: ${msg}`);
  } finally {
    hopeLoading.value = false;
  }
};

const loadHopeSecrets = async (mCode: string) => {
  hopeSecretsLoading.value = true;
  hopeSecrets.value = null;
  hopeSecretsError.value = '';
  showHopeCertPw.value = false;
  showHopeHopePw.value = false;
  try {
    const res = await hopeCertApi.adminGetSecrets(mCode);
    hopeSecrets.value = res.data?.data || null;
  } catch (error: any) {
    hopeSecrets.value = null;
    hopeSecretsError.value = error.response?.data?.message || error.message || '비밀정보 조회 실패';
  } finally {
    hopeSecretsLoading.value = false;
  }
};

const selectHopeCert = async (cert: HopeCertItem) => {
  selectedHopeCert.value = cert;
  if (cert.chc_status === 'active') {
    await loadHopeSecrets(cert.m_code);
  } else {
    hopeSecrets.value = null;
    hopeSecretsError.value = '';
  }
};

const closeHopeSidebar = () => {
  selectedHopeCert.value = null;
  hopeSecrets.value = null;
  hopeSecretsError.value = '';
};

const downloadHopeCertZip = async (item?: HopeCertItem) => {
  const target = item || selectedHopeCert.value;
  if (!target || downloadingHopeCode.value) return;
  downloadingHopeCode.value = target.m_code;
  try {
    const mCode = target.m_code;
    const safeName = String(target.chc_cert_name || target.m_name || mCode)
      .replace(/[^\w가-힣().-]/g, '_');
    await hopeCertApi.adminDownloadZip(mCode, `hope-cert_${safeName}.zip`);
  } catch (error: any) {
    const msg = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`인증서 다운로드 실패: ${msg}`);
  } finally {
    downloadingHopeCode.value = '';
  }
};

const loadLtcsSecrets = async (mCode: string) => {
  ltcsSecretsLoading.value = true;
  ltcsSecrets.value = null;
  ltcsSecretsError.value = '';
  showLtcsCertPw.value = false;
  try {
    const res = await certApi.adminGetSecrets(mCode);
    ltcsSecrets.value = res.data?.data || null;
  } catch (error: any) {
    ltcsSecrets.value = null;
    ltcsSecretsError.value = error.response?.data?.message || error.message || '비밀정보 조회 실패';
  } finally {
    ltcsSecretsLoading.value = false;
  }
};

const downloadLtcsCertZip = async (item?: CertItem) => {
  const target = item || selectedCert.value;
  if (!target || downloadingLtcsCode.value) return;
  if (target.cic_status !== 'active') {
    alert('활성 상태 인증서만 다운로드할 수 있습니다.');
    return;
  }
  downloadingLtcsCode.value = target.m_code;
  try {
    const mCode = target.m_code;
    const safeName = String(target.cic_cert_name || target.m_name || mCode)
      .replace(/[^\w가-힣().-]/g, '_');
    await certApi.adminDownloadZip(mCode, `ltcs-cert_${safeName}.zip`);
  } catch (error: any) {
    const msg = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`인증서 다운로드 실패: ${msg}`);
  } finally {
    downloadingLtcsCode.value = '';
  }
};

const selectCert = async (cert: CertItem) => {
  selectedCert.value = cert;
  loadAllowedIPs(cert.cic_idx);
  if (cert.cic_status === 'active') {
    await loadLtcsSecrets(cert.m_code);
  } else {
    ltcsSecrets.value = null;
    ltcsSecretsError.value = '';
  }
};

const loadAllowedIPs = async (cicIdx: number) => {
  loadingIPs.value = true;
  try {
    const res = await certApi.getAllowedIPs(cicIdx);
    allowedIPs.value = res.data.data || [];
  } catch (error: any) {
    console.error('허용 IP 목록 로드 실패:', error);
    allowedIPs.value = [];
  } finally {
    loadingIPs.value = false;
  }
};

const openIpModal = (cert: CertItem) => {
  selectCert(cert);
};

const openAddIpDialog = () => {
  newIP.value = { ip: '', description: '' };
  showAddIpDialog.value = true;
};

const addIP = async () => {
  if (!newIP.value.ip || !selectedCert.value) return;

  addingIP.value = true;
  try {
    await certApi.addAllowedIP(selectedCert.value.cic_idx, {
      ip: newIP.value.ip.trim(),
      description: newIP.value.description.trim()
    });
    showAddIpDialog.value = false;
    loadAllowedIPs(selectedCert.value.cic_idx);
    loadCerts();
  } catch (error: any) {
    const msg = error.response?.data?.message || '허용 IP 추가에 실패했습니다.';
    alert(msg);
  } finally {
    addingIP.value = false;
  }
};

const confirmDeleteIP = (ip: AllowedIP) => {
  ipToDelete.value = ip;
  showDeleteIpDialog.value = true;
};

const deleteIP = async () => {
  if (!ipToDelete.value || !selectedCert.value) return;

  deletingIP.value = true;
  try {
    await certApi.deleteAllowedIP(ipToDelete.value.ccai_idx);
    showDeleteIpDialog.value = false;
    loadAllowedIPs(selectedCert.value.cic_idx);
    loadCerts();
  } catch (error: any) {
    alert('허용 IP 삭제에 실패했습니다.');
  } finally {
    deletingIP.value = false;
  }
};

const toggleIP = async (ip: AllowedIP, isActive: boolean) => {
  try {
    await certApi.toggleAllowedIP(ip.ccai_idx, isActive);
    ip.ccai_is_active = isActive ? 1 : 0;
  } catch (error: any) {
    alert('상태 변경에 실패했습니다.');
  }
};

const loadMyIP = async () => {
  try {
    const res = await certApi.getMyIP();
    myIP.value = res.data.data?.ip || '';
  } catch (error) {
    console.error('IP 조회 실패:', error);
    myIP.value = '조회 실패';
  }
};

const copyMyIP = () => {
  if (!myIP.value) return;
  
  // Clipboard API 지원 여부 확인
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(myIP.value).then(() => {
      alert(`IP가 복사되었습니다: ${myIP.value}`);
    }).catch(() => {
      fallbackCopy(myIP.value);
    });
  } else {
    fallbackCopy(myIP.value);
  }
};

const fallbackCopy = (text: string) => {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';
  document.body.appendChild(textArea);
  textArea.select();
  try {
    document.execCommand('copy');
    alert(`IP가 복사되었습니다: ${text}`);
  } catch (err) {
    alert(`복사 실패. IP를 직접 복사해주세요: ${text}`);
  }
  document.body.removeChild(textArea);
};

const applyMyIP = () => {
  if (myIP.value) {
    newIP.value.ip = myIP.value;
  }
};

onMounted(() => {
  loadCerts();
  loadHopeCerts();
  loadMyIP();
});

watch(activeTab, (tab) => {
  if (tab === 'hope' && hopeCerts.value.length === 0) {
    loadHopeCerts();
  }
});
</script>
